import { Navigate, Outlet, useLocation, Link } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import type { AppRole } from './AuthProvider';

type PrivateRouteProps = {
  /** 指定した役割のみ通す。未指定ならログイン済みなら通す。 */
  requireRoles?: AppRole[];
};

const PrivateRoute = ({ requireRoles }: PrivateRouteProps) => {
  const { session, loading, role, roleStatus, refreshRole, signOut } = useAuth();
  const location = useLocation();

  if (loading) {
    return <p>認証状態を確認しています...</p>;
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!requireRoles || requireRoles.length === 0) {
    return <Outlet />;
  }

  if (roleStatus === 'idle' || roleStatus === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 px-4">
        <p className="text-sm text-slate-600">権限を確認しています...</p>
      </div>
    );
  }

  // 権限を判定できなかった場合は、誤って開かないように止めたうえで再試行させる。
  if (roleStatus === 'error') {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">権限を確認できませんでした</h1>
          <p className="mt-2 text-sm text-slate-600">
            通信が不安定な可能性があります。もう一度お試しください。
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <button
              onClick={() => void refreshRole()}
              className="inline-flex h-11 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700"
            >
              再試行
            </button>
            <Link
              to="/care"
              className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              ケア業務へ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!role || !requireRoles.includes(role)) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">このページは管理者専用です</h1>
          <p className="mt-2 text-sm text-slate-600">
            勤怠・給与などの労務ページは管理者のみ利用できます。
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <Link
              to="/care"
              className="inline-flex h-11 items-center justify-center rounded-lg bg-cyan-700 px-4 text-sm font-semibold text-white hover:bg-cyan-800"
            >
              ケア業務へ
            </Link>
            <button
              onClick={() => void signOut()}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
